import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getClinicalMedicalHistory,
  getPatient,
  getPatientHistory,
} from '../../api/patients';
import MedicalHistoryBook from './MedicalHistoryBook';
import EhrDemographicsPanel from '../../pages/front_office/components/ehr/EhrDemographicsPanel';
import EhrLoadingSkeleton from '../../pages/front_office/components/ehr/EhrLoadingSkeleton';
import EhrPatientHeader from '../../pages/front_office/components/ehr/EhrPatientHeader';
import EhrStatsCards from '../../pages/front_office/components/ehr/EhrStatsCards';
import EhrVisitTimeline from '../../pages/front_office/components/ehr/EhrVisitTimeline';
import { ehr } from '../../pages/front_office/styles/ehrClasses';
import { computeEhrStats } from '../../pages/front_office/utils/ehrUtils';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'history', label: 'Medical history' },
  { id: 'visits', label: 'Visits' },
  { id: 'profile', label: 'Profile' },
];

export default function PatientMedicalHistoryContent({
  patientId,
  compact = false,
  allowEditConsultations = true,
}) {
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [clinicalHistory, setClinicalHistory] = useState(null);
  const [clinicalLoading, setClinicalLoading] = useState(false);
  const [clinicalError, setClinicalError] = useState('');
  const [clinicalLoaded, setClinicalLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(
    allowEditConsultations ? 'history' : 'overview'
  );

  const reloadAll = useCallback(async () => {
    if (!patientId) return;
    const [p, history, clinical] = await Promise.all([
      getPatient(patientId),
      getPatientHistory(patientId),
      getClinicalMedicalHistory(patientId),
    ]);
    setPatient(p);
    setVisits(Array.isArray(history) ? history : []);
    setClinicalHistory(clinical);
    setClinicalLoaded(true);
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setPatient(null);
      setVisits([]);
      setClinicalHistory(null);
      setClinicalLoaded(false);
      setLoading(false);
      setError('');
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');
    setActiveTab(allowEditConsultations ? 'history' : 'overview');
    setClinicalHistory(null);
    setClinicalLoaded(false);
    setClinicalError('');

    (async () => {
      try {
        const [p, history] = await Promise.all([getPatient(patientId), getPatientHistory(patientId)]);
        if (cancelled) return;
        setPatient(p);
        setVisits(Array.isArray(history) ? history : []);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load medical history');
          setPatient(null);
          setVisits([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  useEffect(() => {
    if (activeTab !== 'history' || !patientId || clinicalLoaded) return undefined;

    let cancelled = false;
    setClinicalLoading(true);
    setClinicalError('');

    getClinicalMedicalHistory(patientId)
      .then((row) => {
        if (!cancelled) {
          setClinicalHistory(row);
          setClinicalLoaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setClinicalError(err.message || 'Failed to load medical history book');
          setClinicalHistory(null);
        }
      })
      .finally(() => {
        if (!cancelled) setClinicalLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, patientId, clinicalLoaded]);

  const stats = useMemo(() => computeEhrStats(visits), [visits]);
  const recentVisits = useMemo(() => visits.slice(0, 3), [visits]);

  if (!patientId) {
    return <p className={ehr.empty}>No patient selected.</p>;
  }

  if (loading) {
    return <EhrLoadingSkeleton />;
  }

  if (error) {
    return (
      <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
        {error}
      </p>
    );
  }

  if (!patient) {
    return <p className={ehr.empty}>Patient record could not be loaded.</p>;
  }

  const pageClass = compact ? 'space-y-5' : ehr.page;

  return (
    <div className={pageClass}>
      <EhrPatientHeader patient={patient} />
      <EhrStatsCards stats={stats} />

      <nav className={ehr.tabBar} aria-label="Medical history sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? ehr.tabActive : ehr.tabInactive}
            aria-current={activeTab === tab.id ? 'page' : undefined}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' ? (
        <div className={ehr.layout}>
          <EhrDemographicsPanel patient={patient} sticky={!compact} />
          <EhrVisitTimeline
            visits={recentVisits}
            title="Recent visits"
            patientId={patientId}
            allowEditConsultations={false}
          />
        </div>
      ) : null}

      {activeTab === 'history' ? (
        <MedicalHistoryBook
          patient={patient}
          history={clinicalHistory}
          loading={clinicalLoading}
          error={clinicalError}
          showStatSummaryButton
          compact={compact}
          editable={allowEditConsultations}
          onRecordUpdated={reloadAll}
        />
      ) : null}

      {activeTab === 'visits' ? (
        <EhrVisitTimeline
          visits={visits}
          patientId={patientId}
          allowEditConsultations={false}
        />
      ) : null}

      {activeTab === 'profile' ? <EhrDemographicsPanel patient={patient} /> : null}

      {activeTab === 'overview' && visits.length > 3 ? (
        <p className="text-center text-sm text-slate-500">
          Showing 3 most recent visits. Open the{' '}
          <button
            type="button"
            className="font-semibold text-teal-700 hover:underline"
            onClick={() => setActiveTab('visits')}
          >
            Visits
          </button>{' '}
          tab for full history.
        </p>
      ) : null}
    </div>
  );
}
